import dayjs, { Dayjs } from 'dayjs';
import apiService from 'library/api-service';
import { useAppSelector } from 'store/hooks';
import { Grid2 as Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers';
import { GridColDef } from '@mui/x-data-grid-premium';
import { FormattedMessage, useIntl } from 'react-intl';
import { LogItem, ShortBook, User } from 'library/types';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { ACTION_TYPE_ENUM, PROCESS_TYPE_ENUM, STORAGE_KEY_ENUM } from 'enums';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { CustomToolbar, ZebraDataGrid, CustomNoRowsOverlay, DataGridBox } from 'components';
import { renderDataGridLocaleText, renderDatePickerLocaleText, renderFormattedDate } from 'utils';

const Timeline = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();

  // redux hooks
  const permissionCodes = useAppSelector(selectPermissionCodes);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [data, setData] = useState<LogItem[]>([]);
  const [books, setBooks] = useState<ShortBook[]>([]);
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(1, 'month'));

  // translate functions
  const getProcessTypeTranslated = useCallback(
    (processType: PROCESS_TYPE_ENUM) =>
      ({
        [PROCESS_TYPE_ENUM.ACCOUNT]: intl.formatMessage({ id: 'enum.processType.account', defaultMessage: 'Hesap' }),
        [PROCESS_TYPE_ENUM.BOOK]: intl.formatMessage({ id: 'enum.processType.book', defaultMessage: 'Proje' }),
        [PROCESS_TYPE_ENUM.CONNECTION_TYPE]: intl.formatMessage({
          id: 'enum.processType.connectionType',
          defaultMessage: 'Bağlantı Türü',
        }),
        [PROCESS_TYPE_ENUM.ENVIRONMENT_TYPE]: intl.formatMessage({
          id: 'enum.processType.environmentType',
          defaultMessage: 'Sunucu Tipi',
        }),
        [PROCESS_TYPE_ENUM.GIB_TYPE]: intl.formatMessage({
          id: 'enum.processType.gibType',
          defaultMessage: 'Gib Yöntemi',
        }),
        [PROCESS_TYPE_ENUM.ME]: intl.formatMessage({ id: 'enum.processType.me', defaultMessage: 'Profil' }),
        [PROCESS_TYPE_ENUM.PERMISSION]: intl.formatMessage({
          id: 'enum.processType.permission',
          defaultMessage: 'İzin',
        }),
        [PROCESS_TYPE_ENUM.PROJECT]: intl.formatMessage({ id: 'enum.processType.project', defaultMessage: 'Ürün' }),
        [PROCESS_TYPE_ENUM.PROJECT_GROUP]: intl.formatMessage({
          id: 'enum.processType.projectGroup',
          defaultMessage: 'Ürün Grubu',
        }),
        [PROCESS_TYPE_ENUM.ROLE]: intl.formatMessage({ id: 'enum.processType.role', defaultMessage: 'Rol' }),
        [PROCESS_TYPE_ENUM.USED_BANK]: intl.formatMessage({
          id: 'enum.processType.usedBank',
          defaultMessage: 'Kullanılan Banka',
        }),
        [PROCESS_TYPE_ENUM.USER]: intl.formatMessage({ id: 'enum.processType.user', defaultMessage: 'Kullanıcı' }),
        [PROCESS_TYPE_ENUM.VERSION]: intl.formatMessage({ id: 'enum.processType.version', defaultMessage: 'Version' }),
        [PROCESS_TYPE_ENUM.EFFORT_REQUEST]: intl.formatMessage({
          id: 'enum.processType.effortRequest',
          defaultMessage: 'Efor Talebi',
        }),
        [PROCESS_TYPE_ENUM.EFFORT_CONTACT]: intl.formatMessage({
          id: 'enum.processType.effortContact',
          defaultMessage: 'Efor Muhatabı',
        }),
        [PROCESS_TYPE_ENUM.EFFORT_STATUS_CONTACT]: intl.formatMessage({
          id: 'enum.processType.effortStatusContact',
          defaultMessage: 'Efor Statü Muhatabı',
        }),
        [PROCESS_TYPE_ENUM.PROJECT_TASK_BASE]: intl.formatMessage({
          id: 'enum.processType.projectTaskBase',
          defaultMessage: 'Ürün - Proje Planı',
        }),
        [PROCESS_TYPE_ENUM.ACTIVITY_TYPE]: intl.formatMessage({
          id: 'enum.processType.activityType',
          defaultMessage: 'Aktivite Tipi',
        }),
        [PROCESS_TYPE_ENUM.PROJECT_TASK]: intl.formatMessage({
          id: 'enum.processType.projectTask',
          defaultMessage: 'Proje Adımı',
        }),
      })[processType],
    [intl]
  );

  const getActionTypeTranslated = useCallback(
    (actionType: ACTION_TYPE_ENUM) =>
      ({
        [ACTION_TYPE_ENUM.INSERT]: intl.formatMessage({ id: 'enum.actionType.insert', defaultMessage: 'Ekleme' }),
        [ACTION_TYPE_ENUM.DELETE]: intl.formatMessage({ id: 'enum.actionType.delete', defaultMessage: 'Silme' }),
        [ACTION_TYPE_ENUM.UPDATE]: intl.formatMessage({ id: 'enum.actionType.update', defaultMessage: 'Güncelleme' }),
        [ACTION_TYPE_ENUM.SIGN_IN]: intl.formatMessage({ id: 'enum.actionType.signIn', defaultMessage: 'Giriş' }),
        [ACTION_TYPE_ENUM.RESTORE]: intl.formatMessage({ id: 'enum.actionType.restore', defaultMessage: 'Yenileme' }),
        [ACTION_TYPE_ENUM.ASSIGN]: intl.formatMessage({ id: 'enum.actionType.assign', defaultMessage: 'Atama' }),
        [ACTION_TYPE_ENUM.SEND_EMAIL]: intl.formatMessage({
          id: 'enum.actionType.sendEmail',
          defaultMessage: 'Hatırlatma E-Postası',
        }),
      })[actionType],
    [intl]
  );

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'createdAt',
        headerName: intl.formatMessage({
          id: 'timeline.createdAt',
          defaultMessage: 'Tarih',
        }),
        align: 'center',
        headerAlign: 'center',
        type: 'date',
        valueGetter: (value: string) => new Date(value),
        renderCell: (params) => renderFormattedDate(params.value, true),
        flex: 0.25,
      },
      {
        field: 'userId',
        headerName: intl.formatMessage({ id: 'timeline.user', defaultMessage: 'Kullanıcı' }),
        valueGetter: (value: number) => {
          const user = users.find((user) => value === user.id);

          return user?.nameSurname ?? '';
        },
        type: 'singleSelect',
        valueOptions: () => users.map((user) => user.nameSurname),
        flex: 0.25,
      },
      {
        field: 'process',
        headerName: intl.formatMessage({ id: 'timeline.process', defaultMessage: 'İşlem' }),
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value: number) => getProcessTypeTranslated(value),
        type: 'singleSelect',
        valueOptions: () =>
          Object.values(PROCESS_TYPE_ENUM)
            .filter((v) => typeof v === 'number')
            .map((v) => getProcessTypeTranslated(v as PROCESS_TYPE_ENUM)),
      },
      {
        field: 'action',
        headerName: intl.formatMessage({ id: 'timeline.action', defaultMessage: 'Eylem' }),
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value: number) => getActionTypeTranslated(value),
        type: 'singleSelect',
        valueOptions: () =>
          Object.values(ACTION_TYPE_ENUM)
            .filter((v) => typeof v === 'number')
            .map((v) => getActionTypeTranslated(v as ACTION_TYPE_ENUM)),
      },
      {
        field: 'bookId',
        headerName: intl.formatMessage({ id: 'timeline.book', defaultMessage: 'Proje' }),
        headerAlign: 'center',
        valueGetter: (value: number | null) => {
          const book = books.find((book) => value === book.id);

          return book?.title ?? '';
        },
        type: 'singleSelect',
        valueOptions: () => books.map((book) => book.title),
        flex: 0.25,
      },
      {
        field: 'explanation',
        headerName: intl.formatMessage({ id: 'timeline.explanation', defaultMessage: 'Açıklama' }),
        flex: 1,
      },
    ],
    [intl, books, users, getActionTypeTranslated, getProcessTypeTranslated]
  );

  // async helper functions
  const getValues = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getTimelineList(startDate, endDate);

      if (response.isSuccess) {
        const duplicateArr = [...response.data];
        const sortedData = duplicateArr.sort((a, b) =>
          new Date(b.createdAt).getTime() > new Date(a.createdAt).getTime() ? 1 : -1
        );

        setData(sortedData);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const getUsers = async () => {
    try {
      const response = await apiService.getUsers();

      if (response.isSuccess) {
        setUsers(response.data);
      }
    } catch {}
  };

  const getBooks = async () => {
    try {
      const response = await apiService.getCompanies();

      if (response.isSuccess) {
        setBooks(response.data);
      }
    } catch {}
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('timeline-page-view')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    getUsers();
    getBooks();
  }, []);

  useEffect(() => {
    getValues();
  }, [getValues]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <Grid size={2}>
          <DatePicker
            label={<FormattedMessage id="timeline.startDate" defaultMessage="Başlangıç Tarihi" />}
            localeText={renderDatePickerLocaleText()}
            disableFuture
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: 'outlined',
              },
            }}
          />
        </Grid>
        <Grid size={2}>
          <DatePicker
            label={<FormattedMessage id="timeline.endDate" defaultMessage="Bitiş Tarihi" />}
            localeText={renderDatePickerLocaleText()}
            disableFuture
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: 'outlined',
              },
            }}
          />
        </Grid>
      </Grid>
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => new Date(row.createdAt).getTime()}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={data}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default Timeline;
